import React from 'react';
import PropTypes from 'prop-types';
import {Chip, Typography, Box} from '@mui/material';
import {Lock, X, Loader2} from 'lucide-react';
import {getStatusDisplayName} from '../../util/documentStatus';
import {INGESTION_STATUSES} from '../../constants';

const DocumentStatusChip = ({
  rowData,
  workflowStatus: propWorkflowStatus,
  orgConfig,
  isPdfReviewAssistant,
}) => {
  if (isPdfReviewAssistant) {
    return (
      <Chip
        size="small"
        label={
          <Typography
            sx={{
              color: '#162523',
              fontSize: '13px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '140%',
              letterSpacing: '-0.12px',
            }}
          >
            Review
          </Typography>
        }
        sx={{
          height: 'auto',
          padding: '3px 12px',
          borderRadius: '1000px',
          background: '#F5CF7F',
          border: 'none',
          '& .MuiChip-label': {
            padding: 0,
            display: 'flex',
            alignItems: 'center',
          },
        }}
      />
    );
  }

  // Handle FAILED rollforward status
  if (rowData?.ingestionState?.status === INGESTION_STATUSES.failed) {
    return (
      <Chip
        size="small"
        label={
          <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
            <X
              size={14}
              color="#EC4B27"
              strokeWidth={2.5}
              style={{flexShrink: 0}}
            />
            <Typography
              sx={{
                color: '#162523',
                fontSize: '13px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '140%',
                letterSpacing: '-0.12px',
              }}
            >
              Failed
            </Typography>
          </div>
        }
        sx={{
          height: 'auto',
          padding: '3px 12px',
          borderRadius: '1000px',
          background: '#F2F4F4',
          border: 'none',
          '& .MuiChip-label': {
            padding: 0,
            display: 'flex',
            alignItems: 'center',
          },
        }}
      />
    );
  }

  // Handle processing states (NOT_STARTED, PENDING, IN_PROGRESS)
  if (
    rowData?.ingestionState?.status &&
    rowData.ingestionState.status !== INGESTION_STATUSES.completed &&
    rowData.ingestionState.status !== INGESTION_STATUSES.failed
  ) {
    let processingText = 'Processing';
    switch (rowData?.ingestionState?.type) {
      case 'upload':
        processingText = 'Processing upload';
        break;
      case 'rollforward':
        processingText = 'Processing roll forward';
        break;
      default:
        processingText = 'Processing';
    }
    return (
      <>
        <style>
          {`
            @keyframes spin {
              from {
                transform: rotate(0deg);
              }
              to {
                transform: rotate(360deg);
              }
            }
          `}
        </style>
        <Chip
          size="small"
          label={
            <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
              <Box
                sx={{
                  display: 'inline-flex',
                  animation: 'spin 1s linear infinite',
                  flexShrink: 0,
                }}
              >
                <Loader2 size={14} color="#34A290" strokeWidth={2.5} />
              </Box>
              <Typography
                sx={{
                  color: '#162523',
                  fontSize: '13px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '140%',
                  letterSpacing: '-0.12px',
                }}
              >
                {processingText}
              </Typography>
            </div>
          }
          sx={{
            height: 'auto',
            padding: '3px 12px',
            borderRadius: '1000px',
            background: '#F2F4F4',
            border: 'none',
            '& .MuiChip-label': {
              padding: 0,
              display: 'flex',
              alignItems: 'center',
            },
          }}
        />
      </>
    );
  }

  // Handle workflow status
  const workflowStatus = propWorkflowStatus || rowData?.workflowStatus;

  if (workflowStatus && orgConfig?.documentWorkflowStatuses) {
    // Sort options by sortOrder
    const sortedOptions = [...orgConfig.documentWorkflowStatuses].sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
    );

    const selectedOption = sortedOptions.find(
      (opt) => opt.id === workflowStatus
    );

    if (selectedOption) {
      const totalOptions = sortedOptions.length;
      const selectedIndex = sortedOptions.findIndex(
        (opt) => opt.id === workflowStatus
      );
      const isSecondToLast = selectedIndex === totalOptions - 2;
      const isLast = selectedIndex === totalOptions - 1;
      const progressStatuses = Math.max(1, totalOptions - 1);
      const selectedFillPercentage =
        selectedIndex >= 0 && !isSecondToLast && !isLast
          ? Math.min(100, ((selectedIndex + 1) / progressStatuses) * 100)
          : 0;

      const circleColor = selectedOption?.color ?? '#34A290';
      const isFullyFilled = isSecondToLast || isLast;
      const displayName = getStatusDisplayName(
        {workflowStatus},
        orgConfig.documentWorkflowStatuses
      );

      let statusLabel = displayName || workflowStatus;
      if (isLast && rowData?.scheduledLockDate) {
        statusLabel = `${statusLabel} - ${new Date(rowData.scheduledLockDate).toLocaleDateString()}`;
      }

      return (
        <Chip
          size="small"
          label={
            <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
              <div
                style={{
                  display: 'flex',
                  width: '12px',
                  height: '12px',
                  padding: isFullyFilled ? 0 : '1px',
                  alignItems: 'center',
                  justifyContent: 'center',
                  aspectRatio: '1/1',
                  borderRadius: '1000px',
                  border: `1px solid ${circleColor}`,
                  background: isFullyFilled ? circleColor : '#FFF',
                  flexShrink: 0,
                  position: 'relative',
                }}
              >
                {!isFullyFilled && (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '1000px',
                      background: `conic-gradient(from 0deg, ${circleColor} 0% ${selectedFillPercentage}%, transparent ${selectedFillPercentage}% 100%)`,
                    }}
                  />
                )}
                {isSecondToLast && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="8"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
                {isLast && (
                  <Lock
                    size={8}
                    color="white"
                    strokeWidth={2.5}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                )}
              </div>
              <Typography
                sx={{
                  color: '#162523',
                  fontSize: '13px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '140%',
                  letterSpacing: '-0.12px',
                }}
              >
                {statusLabel}
              </Typography>
            </div>
          }
          sx={{
            height: 'auto',
            padding: '3px 12px',
            borderRadius: '1000px',
            background: '#F2F4F4',
            border: 'none',
            '& .MuiChip-label': {
              padding: 0,
              display: 'flex',
              alignItems: 'center',
            },
          }}
        />
      );
    }
  }

  // Fallback: return null if no status found
  return null;
};

DocumentStatusChip.propTypes = {
  isPdfReviewAssistant: PropTypes.bool,
  rowData: PropTypes.shape({
    ingestionState: PropTypes.shape({
      type: PropTypes.string,
      status: PropTypes.string,
      source: PropTypes.string,
    }),
    status: PropTypes.string,
    workflowStatus: PropTypes.string,
    scheduledLockDate: PropTypes.string,
  }),
  workflowStatus: PropTypes.string,
  orgConfig: PropTypes.shape({
    documentWorkflowStatuses: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        sortOrder: PropTypes.number,
        color: PropTypes.string,
      })
    ),
  }),
};

export default DocumentStatusChip;
